import cx from 'classnames'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { copyToClipboard } from '@lib/helpers'
import { PrintShopFormContext } from '@lib/print/print-shop-form-context'
import { PrintShopFormStringsContext } from '@lib/print/print-shop-form-strings-context'
import { getPageUrl, PageType } from '@lib/routes'
import { SiteContext } from '@lib/site'

import Button from '@components/buttons/button'
import ComplexPortableText from '@components/portable-text/complex'

const ReviewStepShopLink = () => {
  const { settings } = useContext(SiteContext)
  const { shopState } = useContext(PrintShopFormContext)
  const { shopFormStrings } = useContext(PrintShopFormStringsContext)

  const [isLinkCopied, setIsLinkCopied] = useState(false)

  const shopUrl = useMemo(() => {
    if (!shopState.slug) {
      return ''
    }

    const pageUrl = getPageUrl(PageType.PRIVATE_SHOP_PAGE, [shopState.slug])

    return `${settings?.siteURL}${pageUrl}`
  }, [settings?.siteURL, shopState.id])

  const handleCopyUrlClick = useCallback(() => {
    copyToClipboard(shopUrl)

    setIsLinkCopied(true)
  }, [shopUrl])

  // Unset URL copied status after 3 seconds
  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined

    if (isLinkCopied) {
      timeout = setTimeout(() => {
        setIsLinkCopied(false)
      }, 3000)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [isLinkCopied])

  return (
    <div className="px-5 sm:px-8 py-8 border border-gray-light bg-white space-y-8">
      <div className="space-y-5">
        <h2 className="leading-tight font-semibold text-[32px] text-center">
          {shopFormStrings.shopFormReviewStepUrlTitle
            ?.replace(/{school_name}/gi, shopState.institutionName ?? '')
            ?.replace(/{shop_name}/gi, shopState.shopName ?? '')}
        </h2>

        {!!shopFormStrings.shopFormReviewStepUrlDescription && (
          <ComplexPortableText
            content={shopFormStrings.shopFormReviewStepUrlDescription}
            className="rc text-center"
          />
        )}
      </div>

      {!!shopUrl && (
        <div className="flex items-center justify-center gap-2.5 sm:gap-5">
          <input
            value={shopUrl}
            readOnly
            className={cx(
              'w-full max-w-[300px] py-3 px-4',
              'border border-gray-dark rounded',
              'leading-snug text-lg text-gray-dark',
            )}
          />

          <Button
            variant="primary"
            color="blue"
            size="normal-small"
            icon="Copy"
            disabled={isLinkCopied}
            onClick={handleCopyUrlClick}
            className="whitespace-nowrap"
          >
            {isLinkCopied
              ? shopFormStrings.shopFormUrlCopyLinkButtonCopied
              : shopFormStrings.shopFormUrlCopyLinkButton}
          </Button>
        </div>
      )}
    </div>
  )
}

export default ReviewStepShopLink
